import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Airplane, FileText, MapTrifold, GraduationCap, Buildings,
  ShieldCheck, DeviceMobile, CheckCircle, ArrowRight, Globe,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { PageHero } from '@/components/common/Sections';
import { CTABanner } from '@/components/common/Cards';
import SEO from '@/components/common/SEO';
import { SERVICE_CATEGORIES, VISA_COUNTRIES, IMAGES } from '@/lib/constants';

const ICON_MAP = {
  Airplane, FileText, MapTrifold, GraduationCap, Buildings,
  ShieldCheck, DeviceMobile, Passport: FileText, Globe,
};

export default function ServicesPage() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.replace('#', ''));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
      }
    }
  }, [location.hash]);

  return (
    <div className="page-transition">
      <SEO title="Services" description="Osaka Group's complete travel services: air ticketing, forex, visa & passport processing for 120+ countries, tour packages, study abroad, corporate travel, insurance, document attestation & Osaka Connect digital platform." path="/services" />
      <PageHero
        subtitle="Everything you need for travel, immigration & beyond — all under one roof"
        backgroundImage={IMAGES.airport}
      />

      {/* Quick Nav */}
      <section data-testid="services-quick-nav" className="bg-[#0A1F3D] border-t border-white/5">
        <div className="container-custom py-6 overflow-x-auto">
          <div className="flex gap-3 min-w-max">
            {SERVICE_CATEGORIES.map((cat) => (
              <a
                key={cat.id}
                href={`#${cat.id}`}
                data-testid={`nav-${cat.id}`}
                className="px-4 py-2 text-sm text-white/60 hover:text-[#D4AF37] border border-white/10 hover:border-[#D4AF37]/30 transition-colors whitespace-nowrap"
              >
                {cat.title.split('(')[0].split('/')[0].trim()}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Service Categories */}
      {SERVICE_CATEGORIES.map((category, catIndex) => {
        const IconComponent = ICON_MAP[category.icon] || FileText;
        const isEven = catIndex % 2 === 0;
        const isDark = catIndex % 3 === 0;

        return (
          <section
            key={category.id}
            id={category.id}
            data-testid={`service-section-${category.id}`}
            className={`section-padding ${isDark ? 'bg-[#0A1F3D] relative grain-overlay' : isEven ? 'bg-white' : 'bg-[#F5F5F5]'}`}
          >
            <div className="container-custom relative z-10">
              {/* Category with image */}
              {category.image ? (
                <div className={`grid lg:grid-cols-2 gap-16 items-center ${catIndex % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}>
                  {/* Text */}
                  <motion.div
                    initial={{ opacity: 0, x: catIndex % 2 === 0 ? -30 : 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className={catIndex % 2 !== 0 ? 'lg:order-2' : ''}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 flex items-center justify-center border" style={{ borderColor: `${category.color}40` }}>
                        <IconComponent size={22} style={{ color: isDark ? '#D4AF37' : category.color }} />
                      </div>
                      <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#D4AF37]">
                        {String(catIndex + 1).padStart(2, '0')}
                      </span>
                    </div>

                    <h2 className={`text-3xl md:text-4xl font-bold font-['Playfair_Display'] tracking-tight mb-3 ${isDark ? 'text-white' : 'text-[#0A1F3D]'}`}>
                      {category.title}
                    </h2>
                    <p className={`text-base mb-8 ${isDark ? 'text-[#D4AF37]/80' : 'text-[#D4AF37]'} font-medium`}>
                      {category.tagline}
                    </p>

                    <ul className="space-y-3 mb-8">
                      {category.services.map((service, i) => (
                        <motion.li
                          key={i}
                          initial={{ opacity: 0, x: -10 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: i * 0.05 }}
                          className="flex items-start gap-3"
                        >
                          <CheckCircle size={18} weight="bold" className={`flex-shrink-0 mt-0.5 ${isDark ? 'text-[#D4AF37]' : 'text-[#20A39E]'}`} />
                          <span className={`text-sm leading-relaxed ${isDark ? 'text-white/70' : 'text-[#4A5568]'}`}>
                            {service}
                          </span>
                        </motion.li>
                      ))}
                    </ul>

                    <Link to="/contact">
                      <Button
                        data-testid={`cta-${category.id}`}
                        className={`rounded-none px-6 py-5 font-semibold ${
                          isDark
                            ? 'bg-[#D4AF37] text-[#0A1F3D] hover:bg-[#c5a033]'
                            : 'bg-[#0A1F3D] text-white hover:bg-[#0f294d]'
                        }`}
                      >
                        Enquire Now
                        <ArrowRight size={18} className="ml-2" />
                      </Button>
                    </Link>
                  </motion.div>

                  {/* Image */}
                  <motion.div
                    initial={{ opacity: 0, x: catIndex % 2 === 0 ? 30 : -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className={`relative ${catIndex % 2 !== 0 ? 'lg:order-1' : ''}`}
                  >
                    <div className="img-zoom relative">
                      <img
                        src={category.image}
                        alt={category.title}
                        className="w-full aspect-[4/3] object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F3D]/30 via-transparent to-transparent" />
                    </div>
                    {/* Accent decoration */}
                    <div
                      className="absolute -bottom-3 -right-3 w-full h-full border-2 -z-10"
                      style={{ borderColor: `${category.color}20` }}
                    />
                  </motion.div>
                </div>
              ) : (
                /* Category without image — card grid */
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 flex items-center justify-center border" style={{ borderColor: `${category.color}40` }}>
                      <IconComponent size={22} style={{ color: isDark ? '#D4AF37' : category.color }} />
                    </div>
                    <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#D4AF37]">
                      {String(catIndex + 1).padStart(2, '0')}
                    </span>
                  </div>

                  <h2 className={`text-3xl md:text-4xl font-bold font-['Playfair_Display'] tracking-tight mb-3 ${isDark ? 'text-white' : 'text-[#0A1F3D]'}`}>
                    {category.title}
                  </h2>
                  <p className={`text-base mb-10 max-w-xl ${isDark ? 'text-[#D4AF37]/80' : 'text-[#D4AF37]'} font-medium`}>
                    {category.tagline}
                  </p>

                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {category.services.map((service, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.07 }}
                        className={`p-5 border ${
                          isDark
                            ? 'bg-white/5 border-white/10 hover:bg-white/10'
                            : 'bg-white border-slate-200 hover:shadow-lg'
                        } transition-all duration-300`}
                      >
                        <CheckCircle size={20} weight="bold" className={`mb-3 ${isDark ? 'text-[#D4AF37]' : 'text-[#20A39E]'}`} />
                        <p className={`text-sm leading-relaxed ${isDark ? 'text-white/80' : 'text-[#4A5568]'}`}>
                          {service}
                        </p>
                      </motion.div>
                    ))}
                  </div>

                  <div className="mt-8">
                    <Link to="/contact">
                      <Button
                        data-testid={`cta-${category.id}`}
                        className={`rounded-none px-6 py-5 font-semibold ${
                          isDark
                            ? 'bg-[#D4AF37] text-[#0A1F3D] hover:bg-[#c5a033]'
                            : 'bg-[#0A1F3D] text-white hover:bg-[#0f294d]'
                        }`}
                      >
                        Enquire Now
                        <ArrowRight size={18} className="ml-2" />
                      </Button>
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </section>
        );
      })}

      {/* Visa Countries Quick Reference */}
      <section data-testid="visa-countries" className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#D4AF37] mb-4 block">
              Popular Destinations
            </span>
            <h2 className="text-3xl md:text-4xl font-bold font-['Playfair_Display'] text-[#0A1F3D] tracking-tight mb-4">
              Visa Processing for 120+ Countries
            </h2>
            <p className="text-[#4A5568]">
              Quick processing times. High approval rates. Expert guidance at every step.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {VISA_COUNTRIES.map((country, index) => (
              <motion.div
                key={country.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                data-testid={`visa-card-${country.id}`}
                className="group bg-[#F5F5F5] border border-slate-200 p-5 text-center hover:bg-[#0A1F3D] hover:border-[#0A1F3D] transition-colors duration-300 cursor-pointer"
              >
                <span className="text-3xl block mb-2">{country.flag}</span>
                <h3 className="font-bold font-['Playfair_Display'] text-[#0A1F3D] group-hover:text-white transition-colors text-sm">
                  {country.name}
                </h3>
                <p className="text-xs text-[#4A5568] group-hover:text-[#D4AF37] transition-colors mt-1">
                  {country.processingTime}
                </p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link to="/contact" className="text-[#D4AF37] font-medium text-sm hover:underline">
              Need a visa for another country? Contact us for assistance
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTABanner />
    </div>
  );
}
